import { callGemini } from './api';
import { fetchServerKeys } from './api';
import { getGeminiKey, saveTranscript, saveUndoState, state } from './state';
import { renderTranscript } from './transcript';
import { $, confirmModal, showToast } from './ui';

let aiProposedChanges: Array<{ id: number; text: string }> | null = null;
let pendingAfterSettings: (() => void) | null = null;

export function setPendingAfterSettings(fn: (() => void) | null): void {
  pendingAfterSettings = fn;
}

export function consumePendingAfterSettings(): (() => void) | null {
  const fn = pendingAfterSettings;
  pendingAfterSettings = null;
  return fn;
}

function updateUndoButton(): void {
  const btn = $<HTMLButtonElement>('undoBtn');
  btn.disabled = state.undoStack.length === 0;
}

export function setupEditMode(): void {
  const editBtn = $('editModeToggleBtn');
  const copyBtn = $('copyBtn');
  const editToolbar = $('editToolbar');
  const exitBtn = $('exitEditBtn');
  const undoBtn = $<HTMLButtonElement>('undoBtn');
  const selectAllBtn = $('selectAllBtn');
  const deleteSelectedBtn = $('deleteSelectedBtn');

  editBtn.addEventListener('click', () => {
    state.isEditMode = true;
    state.selectedBubbles.clear();
    state.undoStack = [];
    updateUndoButton();
    editBtn.style.display = 'none';
    copyBtn.style.display = 'none';
    editToolbar.style.display = 'flex';
    renderTranscript();
  });

  exitBtn.addEventListener('click', () => {
    state.isEditMode = false;
    state.selectedBubbles.clear();
    state.undoStack = [];
    editBtn.style.display = 'flex';
    copyBtn.style.display = 'flex';
    editToolbar.style.display = 'none';
    renderTranscript();
  });

  undoBtn.addEventListener('click', () => {
    const prev = state.undoStack.pop();
    if (prev) {
      state.paragraphs = prev;
      state.selectedBubbles.clear();
      saveTranscript();
      renderTranscript();
      updateUndoButton();
    }
  });

  selectAllBtn.addEventListener('click', () => {
    if (state.selectedBubbles.size === state.paragraphs.length) {
      state.selectedBubbles.clear();
    } else {
      state.paragraphs.forEach((_, i) => state.selectedBubbles.add(i));
    }
    renderTranscript();
  });

  deleteSelectedBtn.addEventListener('click', () => {
    void deleteSelectedBubbles();
  });

  setupAiRewrite();
}

async function deleteSelectedBubbles(): Promise<void> {
  const count = state.selectedBubbles.size;
  if (count === 0) {
    showToast('Selecciona al menos un bocadillo para borrar.', 'warning');
    return;
  }
  const ok = await confirmModal({
    title: 'Borrar bocadillos seleccionados',
    message: `¿Borrar ${count} bocadillo${count === 1 ? '' : 's'} seleccionado${count === 1 ? '' : 's'}? Esta acción se puede deshacer.`,
    confirmLabel: 'Borrar',
    cancelLabel: 'Cancelar',
    danger: true,
  });
  if (!ok) return;
  saveUndoState();
  const toRemove = new Set(state.selectedBubbles);
  state.paragraphs = state.paragraphs.filter((_, i) => !toRemove.has(i));
  state.selectedBubbles.clear();
  saveTranscript();
  renderTranscript();
  updateUndoButton();
  showToast(`Borrados ${count} bocadillo${count === 1 ? '' : 's'}.`, 'success');
}

function setupAiRewrite(): void {
  const btn = $<HTMLButtonElement>('aiRewriteBtn');
  btn.addEventListener('click', () => {
    void runAiRewrite();
  });

  const cancelBtn = $('aiReviewCancelBtn');
  cancelBtn.addEventListener('click', () => {
    aiProposedChanges = null;
    $('aiReviewModal').classList.remove('active');
  });

  const applyBtn = $('aiReviewApplyBtn');
  applyBtn.addEventListener('click', () => {
    if (aiProposedChanges) {
      saveUndoState();
      for (const change of aiProposedChanges) {
        if (state.paragraphs[change.id]) {
          state.paragraphs[change.id].text = change.text;
        }
      }
      saveTranscript();
      state.selectedBubbles.clear();
      renderTranscript();
      updateUndoButton();
    }
    $('aiReviewModal').classList.remove('active');
  });
}

async function runAiRewrite(): Promise<void> {
  if (state.selectedBubbles.size === 0) {
    showToast('Selecciona al menos un bocadillo para mejorar.', 'warning');
    return;
  }

  let geminiKey = getGeminiKey();
  if (!geminiKey) {
    const serverKeys = await fetchServerKeys();
    if (serverKeys?.gemini) {
      geminiKey = serverKeys.gemini;
    } else {
      pendingAfterSettings = () => void runAiRewrite();
      $('settingsModal').classList.add('active');
      return;
    }
  }

  const selected = [...state.selectedBubbles].sort((a, b) => a - b);
  const dataToSend = selected.map((idx) => ({ id: idx, text: state.paragraphs[idx].text }));

  const prompt = `
Eres un editor experto de textos.
He transcrito un audio a texto y tiene algunos errores de gramática, cosas que no tienen sentido porque se entendieron mal, y mala puntuación.
Reescribe estos fragmentos de forma que tengan perfecto sentido, corrige gramática y ortografía, pero mantén el estilo y el significado lo más parecido al original.
Devuelve ÚNICAMENTE un JSON con el siguiente formato, sin markdown ni explicaciones:
[
  { "id": ID_ORIGINAL, "text": "TEXTO_CORREGIDO" }
]

Fragmentos:
${JSON.stringify(dataToSend, null, 2)}
`;

  const btn = $<HTMLButtonElement>('aiRewriteBtn');
  const originalHtml = btn.innerHTML;
  btn.innerHTML = `<span class="material-symbols-outlined text-sm animate-spin">refresh</span> Mejorando...`;
  btn.disabled = true;

  try {
    let aiText = await callGemini(geminiKey, prompt);
    aiText = aiText
      .replace(/```json/g, '')
      .replace(/```/g, '')
      .trim();
    const parsed = JSON.parse(aiText) as Array<{ id: number; text: string }>;
    aiProposedChanges = parsed;
    showAiReviewModal(parsed);
  } catch (err) {
    console.error(err);
    showToast(`Error Gemini: ${(err as Error).message}`, 'error');
  } finally {
    btn.innerHTML = originalHtml;
    btn.disabled = false;
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function showAiReviewModal(changes: Array<{ id: number; text: string }>): void {
  const content = $('aiReviewContent');
  content.innerHTML = '';

  for (const change of changes) {
    const original = state.paragraphs[change.id]?.text ?? '';
    const div = document.createElement('div');
    div.className =
      'grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-outline-variant/30 last:border-0 last:pb-0 last:mb-0';
    div.innerHTML = `
      <div>
        <p class="font-bold text-xs text-error mb-1 uppercase tracking-wider">Original</p>
        <div class="bg-error/5 p-3 rounded-xl border border-error/20 text-on-surface font-body-md text-sm">${escapeHtml(original)}</div>
      </div>
      <div>
        <p class="font-bold text-xs text-green-600 mb-1 uppercase tracking-wider">Propuesta IA</p>
        <div class="bg-green-500/5 p-3 rounded-xl border border-green-500/20 text-on-surface font-body-md text-sm font-medium">${escapeHtml(change.text)}</div>
      </div>
    `;
    content.appendChild(div);
  }

  $('aiReviewModal').classList.add('active');
}

export { updateUndoButton };
